import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { ReactElement } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import { pageDetailsPath } from 'app/pages/PlusWebsites/paths';

import { PageSectionEditor } from '../components/PageSectionEditor';
import { useCreateSection } from './hooks/useCreateSection';
import { useGetSectionTypeVersion } from './hooks/useGetSectionTypeVersion';

interface LocationState {
  sectionDisplayName?: string;
  sectionTypeId?: string;
  sectionTypeVersion?: number;
}

const CreateSectionPage = (): ReactElement => {
  const navigate = useNavigate();
  const { pageId = '' } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const location = useLocation();
  const {
    sectionDisplayName = 'Section',
    sectionTypeId = '',
    sectionTypeVersion = 1,
  } = (location.state as LocationState | null) ?? {};

  const { mutateAsync: createSectionAsync, isPending: isSaving } = useCreateSection();
  const { sectionTypeData, isLoading, isError, error } = useGetSectionTypeVersion({
    id: sectionTypeId,
    version: sectionTypeVersion,
  });

  if (!pageId || !websiteId || !sectionTypeId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot create section without valid page and section type information"
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load section details"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  return (
    <PageSectionEditor
      title={`Add ${sectionDisplayName}`}
      schema={(sectionTypeData?.jsonSchema ?? {}) as RJSFSchema}
      uiSchema={(sectionTypeData?.uiSchema ?? {}) as UiSchema}
      isLoading={isLoading}
      isSaving={isSaving}
      isNew
      previewSectionId={undefined}
      previewSectionType={sectionTypeId}
      onSubmit={(formData) =>
        createSectionAsync(
          { websiteId, pageId, sectionTypeId, version: sectionTypeVersion, data: formData },
          {
            // The form is spent once the section exists, so it takes the place of the
            // create entry rather than leaving it behind for the back button to find.
            onSuccess: () => navigate(pageDetailsPath(pageId, searchParams), { replace: true }),
          }
        )
      }
    />
  );
};

export default CreateSectionPage;
